import { useLayoutEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import useIntersectionObserver from '~/hooks/use-intersection-observer';
import { getNumberOfColumns, GAP } from './utilities/virtualized-game-items-utils';
import { selectBreakpoints } from '~/components/brand/desktop-state-manager';

const ROWS_IN_CHUNK = 5;

const splitGamesInChunks = <T,>(games: T[], numberOfColumns: number) => {
	const itemsInChunk = numberOfColumns * ROWS_IN_CHUNK;

	const splitted: T[][] = [];

	for (let i = 0; i < games.length; i += itemsInChunk) {
		splitted.push(games.slice(i, i + itemsInChunk));
	}

	return splitted;
};

const GamesGrid = styled.div<{ numberOfColumns: number; minHeight: number }>`
	display: grid;
	grid-template-columns: repeat(${props => props.numberOfColumns}, 1fr);
	min-height: ${props => `${props.minHeight}px`};
	gap: ${GAP}px;

	&:not(:last-of-type) {
		margin-bottom: ${GAP}px;
	}
`;

const GameWrapper = styled.div<{ itemWidth: number }>`
	height: ${props => `${props.itemWidth}px`};
	width: ${props => `${props.itemWidth}px`};
`;

const ChunkRenderer = <T extends { code: string }>({
	GameItemComponent,
	games,
	numberOfColumns,
	itemWidth,
	chunkIndex
}: {
	GameItemComponent: (props: { game: T; index: number; loading: string }) => JSX.Element | null;
	games: T[];
	numberOfColumns: number;
	itemWidth: number;
	chunkIndex: number;
}) => {
	const chunkRef = useRef(null);
	const hasIOSupport = !!window.IntersectionObserver;
	const entry = useIntersectionObserver(chunkRef, {
		root: document.querySelector('.desktop-scrollbar')?.firstElementChild,
		rootMargin: '10%',
		shouldFreezeOnceVisible: true
	});
	const isVisible = !!entry?.isIntersecting || !hasIOSupport;

	const gridHeight = (itemWidth + GAP) * Math.ceil(games.length / numberOfColumns);

	return (
		<GamesGrid ref={chunkRef} numberOfColumns={numberOfColumns} minHeight={gridHeight}>
			{isVisible &&
				games.map((game, index) => (
					<GameWrapper key={game.code} itemWidth={itemWidth}>
						<GameItemComponent
							game={game}
							index={chunkIndex * numberOfColumns * ROWS_IN_CHUNK + index}
							loading="eager"
						/>
					</GameWrapper>
				))}
		</GamesGrid>
	);
};

const VirtualizedGameItemsContainer = <T extends { code: string }>({
	GameItemComponent,
	games
}: {
	GameItemComponent: (props: { game: T; index: number; loading: string }) => JSX.Element | null;
	games: T[];
}) => {
	const breakPoints = useSelector(selectBreakpoints) as Record<string, number>;
	const initialDimensions = useMemo(() => getNumberOfColumns(breakPoints), [breakPoints]);
	const [numberOfColumns, setNumberOfColumns] = useState(initialDimensions.numberOfColumnsWeCanFit);
	const [itemWidth, setItemWidth] = useState(initialDimensions.calculatedItemWidth);

	// Splitting games into chunks to reduce number on DOM elements
	const splittedGames = useMemo(() => splitGamesInChunks(games, numberOfColumns), [games, numberOfColumns]);

	// Recalculating number of columns on resize
	useLayoutEffect(() => {
		const handleResize = () => {
			const { numberOfColumnsWeCanFit, calculatedItemWidth } = getNumberOfColumns(breakPoints);
			setNumberOfColumns(numberOfColumnsWeCanFit);
			setItemWidth(calculatedItemWidth);
		};

		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [breakPoints]);

	return (
		<>
			{splittedGames.map((gameChunk, index) => (
				<ChunkRenderer
					key={`${gameChunk[0].code}-chunk`}
					games={gameChunk}
					GameItemComponent={GameItemComponent}
					numberOfColumns={numberOfColumns}
					itemWidth={itemWidth}
					chunkIndex={index}
				/>
			))}
		</>
	);
};

export default VirtualizedGameItemsContainer;
